import React, { useRef, useState } from "react";
import { FiUploadCloud } from "react-icons/fi";

/**
 * Zone d'import générique (glisser-déposer + clic) utilisée par les deux sections de la
 * page Sauvegardes : archives base de données (.cbak / .cbak.enc) et archives médias (.zip).
 * Le composant ne téléverse rien lui-même : il remet le fichier choisi au parent via
 * `onFile` et affiche, le cas échéant, la progression d'envoi fournie par le parent.
 */
interface UploadDropzoneProps {
	/** Extensions acceptées, format attribut HTML (ex. ".cbak,.cbak.enc"). */
	accept: string;
	/** Ligne principale de la zone (ex. « Déposez une archive .cbak ici… »). */
	label: string;
	/** Ligne secondaire optionnelle (formats acceptés, avertissement…). */
	hint?: string;
	disabled?: boolean;
	/** Pourcentage d'envoi (0-100) ; null/undefined = aucun envoi en cours. */
	progress?: number | null;
	/** Libellé accessible de l'input fichier caché (utilisé aussi par les tests). */
	inputLabel: string;
	onFile: (file: File) => void;
}

/** Vérifie l'extension du fichier déposé contre la liste `accept` (insensible à la casse). */
const matchesAccept = (fileName: string, accept: string): boolean => {
	const lower = fileName.toLowerCase();
	return accept
		.split(",")
		.map((ext) => ext.trim().toLowerCase())
		.filter(Boolean)
		.some((ext) => lower.endsWith(ext));
};

const UploadDropzone: React.FC<UploadDropzoneProps> = ({ accept, label, hint, disabled, progress, inputLabel, onFile }) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [dragActive, setDragActive] = useState(false);
	const [rejected, setRejected] = useState<string | null>(null);
	const uploading = progress != null;

	const handleFile = (file: File | undefined | null) => {
		if (!file || disabled || uploading) return;
		if (!matchesAccept(file.name, accept)) {
			setRejected(`Format non pris en charge : « ${file.name} ». Formats acceptés : ${accept}.`);
			return;
		}
		setRejected(null);
		onFile(file);
	};

	return (
		<div>
			<div
				role="button"
				tabIndex={disabled ? -1 : 0}
				aria-disabled={disabled || uploading || undefined}
				onClick={() => !disabled && !uploading && inputRef.current?.click()}
				onKeyDown={(e) => {
					if ((e.key === "Enter" || e.key === " ") && !disabled && !uploading) inputRef.current?.click();
				}}
				onDragOver={(e) => {
					e.preventDefault();
					if (!disabled && !uploading) setDragActive(true);
				}}
				onDragLeave={() => setDragActive(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragActive(false);
					handleFile(e.dataTransfer?.files?.[0]);
				}}
				className={[
					"flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors",
					dragActive ? "border-sky-400 bg-sky-50" : "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100",
					disabled || uploading ? "cursor-not-allowed opacity-60" : "",
				].join(" ")}
			>
				<FiUploadCloud className="h-6 w-6 text-slate-400" aria-hidden />
				<p className="text-sm font-medium text-slate-700">{label}</p>
				{hint && <p className="text-xs text-slate-500">{hint}</p>}
				<input
					ref={inputRef}
					type="file"
					accept={accept}
					aria-label={inputLabel}
					disabled={disabled || uploading}
					className="hidden"
					onChange={(e) => {
						handleFile(e.target.files?.[0]);
						// Autorise la re-sélection du même fichier après un échec.
						e.target.value = "";
					}}
				/>
			</div>

			{uploading && (
				<div className="mt-2 space-y-1">
					<div
						className="h-2 w-full overflow-hidden rounded-full bg-slate-100"
						role="progressbar"
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={progress ?? 0}
					>
						<div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${progress ?? 0}%` }} />
					</div>
					<p className="text-xs text-slate-600">Envoi en cours… {progress ?? 0} %</p>
				</div>
			)}

			{rejected && <p className="mt-2 text-xs font-medium text-red-600">{rejected}</p>}
		</div>
	);
};

export default UploadDropzone;
